import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { deliveryAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { RefreshCw, CheckCircle, Truck } from 'lucide-react';

export default function Delivery() {
  const [partners, setPartners] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await deliveryAPI.getPending(page, 10);
      setPartners(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
      setTotal(res.data.totalElements || 0);
    } catch {
      toast('Failed to load delivery partners', 'error');
    } finally {
      setLoading(false);
    }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id, name) => {
    setProcessingId(id);
    try {
      await deliveryAPI.approve(id);
      toast(`${name} approved as delivery partner`, 'success');
      setPartners(p => p.filter(x => x.id !== id));
      setTotal(t => t - 1);
    } catch {
      toast('Failed to approve delivery partner', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <Layout title="Delivery" subtitle="Manage delivery partners">

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Truck size={16} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontWeight: 600, fontSize: 14 }}>Pending Partner Applications</span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>({total})</span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13} /></button>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Vehicle</th>
                <th>Applied On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: 'auto' }} /></td></tr>
              )}
              {!loading && partners.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <Truck size={48} />
                      <h3>No pending applications</h3>
                      <p>All delivery partner applications have been reviewed.</p>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && partners.map(p => (
                <tr key={p.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="avatar">{p.fullName?.[0] || p.name?.[0]}</div>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{p.fullName || p.name}</span>
                    </div>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{p.email}</td>
                  <td>{p.phone}</td>
                  <td>{p.vehicleType || '—'}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                    {p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleApprove(p.id, p.fullName || p.name)}
                      disabled={processingId === p.id}
                    >
                      <CheckCircle size={13} />
                      {processingId === p.id ? 'Approving...' : 'Approve'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <span>Showing {partners.length} of {total}</span>
          <div className="pagination-controls">
            <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {page + 1} of {Math.max(1, totalPages)}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}